/**
 * wayai send-message — send a test message to a hub (preview or production) and get the agent reply
 *
 * Chat hubs: automatically continues the active conversation (or creates one).
 * Task hubs: creates a new conversation by default. Use -c to continue an existing one.
 *
 * Usage:
 *   wayai send-message "Hello, I need help"                       # send message
 *   wayai send-message -c <conversation_id> "Follow up"           # continue conversation
 *   wayai send-message -f card.jpg "is this plan accepted?"       # send with an attachment
 *   wayai send-message -f a.jpg -f b.pdf                          # attachment-only, repeatable
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import {
  isAudioAttachmentFileName,
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_AUDIO_FILE_BASE64_BYTES,
  MAX_MESSAGE_BODY_BYTES,
  type MessageAttachment,
} from '@wayai/core/contracts';
import { requireAuth } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient } from '../lib/api-client.js';

const asMb = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);

/** Exact encoded size of `bytes` raw bytes: base64 is 4 chars per 3 bytes, padded up. */
const base64Length = (bytes: number) => Math.ceil(bytes / 3) * 4;

/** stat + validate, without reading any bytes — so an oversize set fails before the read. */
function statAttachment(filePath: string): { filePath: string; size: number } {
  let stat: fs.Stats;
  try {
    stat = fs.statSync(filePath);
  } catch {
    console.error(`Error: file not found: ${filePath}`);
    process.exit(1);
  }

  if (stat.isDirectory()) {
    console.error(`Error: not a file: ${filePath}`);
    process.exit(1);
  }

  return { filePath, size: stat.size };
}

function readAttachment(filePath: string, size: number): MessageAttachment {
  const fileName = path.basename(filePath);
  // `.jpg` not `jpg` would reach the server as a bogus extension; strip the dot and
  // drop the field entirely for an extension-less file rather than sending ''.
  const ext = path.extname(fileName).replace(/^\./, '');

  return {
    file_name: fileName,
    file_binary: fs.readFileSync(filePath).toString('base64'),
    file_size: size,
    ...(ext && { file_extension: ext }),
  };
}

function usage(): never {
  console.error('Usage: wayai send-message [options] ["message text"]');
  console.error('');
  console.error('Options:');
  console.error('  -c, --conversation <id>  Continue an existing conversation');
  console.error('  -f, --file <path>        Attach a file (repeatable)');
  console.error('');
  console.error('The message text is optional when at least one --file is attached.');
  console.error('The hub type (chat/task) is determined automatically from the hub.');
  console.error('Chat hubs auto-continue the active conversation (or create one).');
  console.error('Task hubs create a new conversation unless -c is provided.');
  process.exit(1);
}

export async function sendMessageCommand(args: string[]): Promise<void> {
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  // Parse args
  let conversationId: string | undefined;
  let message: string | undefined;
  const filePaths: string[] = [];

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '-c' || arg === '--conversation') {
      conversationId = args[++i];
    } else if (arg === '-f' || arg === '--file') {
      const filePath = args[++i];
      if (!filePath) {
        console.error('Error: --file requires a path');
        process.exit(1);
      }
      filePaths.push(filePath);
    } else if (!arg.startsWith('-')) {
      message = arg;
    }
  }

  // An attachment-only send is valid (a photo with no caption), so the guard is
  // "no content at all" rather than "no text".
  if (!message && filePaths.length === 0) usage();

  if (filePaths.length > MAX_ATTACHMENTS_PER_MESSAGE) {
    console.error(`Error: at most ${MAX_ATTACHMENTS_PER_MESSAGE} files per message (got ${filePaths.length}).`);
    process.exit(1);
  }

  // Two checks against the server's per-REQUEST cap, neither an estimate.
  //
  // First, from stat alone: the base64 payload is a strict lower bound on the body, so
  // if the files already exceed the cap the request cannot succeed — reject without
  // reading a byte, rather than loading (and encoding) hundreds of MB to die anyway.
  // A per-FILE check was the original bug: N files each under the cap still blow the
  // budget once base64 inflates them by 4/3.
  const stats = filePaths.map(statAttachment);
  const filesTotal = stats.reduce((sum, s) => sum + base64Length(s.size), 0);
  const fileDetail = () => stats.map((s) => `${path.basename(s.filePath)} ${asMb(s.size)} MB`).join(', ');
  if (filesTotal > MAX_MESSAGE_BODY_BYTES) {
    console.error(
      `Error: ${fileDetail()} encode to ~${asMb(filesTotal)} MB, over the ${asMb(MAX_MESSAGE_BODY_BYTES)} MB per-request limit.`,
    );
    console.error('Base64 inflates a file by about a third, so ~7 MB of files is the practical ceiling.');
    process.exit(1);
  }

  // Audio pre-flight, mirroring the server's pre-write guards (contracts/common.ts):
  // audio routes through the hub's STT connection, so at most ONE audio file per
  // message (only the first would be transcribed) and each within the STT cap.
  // Stat-only, like the body check above — name the offending file before reading it.
  const audioStats = stats.filter((s) => isAudioAttachmentFileName(path.basename(s.filePath)));
  if (audioStats.length > 1) {
    console.error(
      `Error: at most one audio file per message (got ${audioStats.length}: ${audioStats.map((s) => path.basename(s.filePath)).join(', ')}).`,
    );
    console.error('Audio is transcribed via the hub\'s STT connection, and only one file per message is transcribed.');
    process.exit(1);
  }
  const oversizeAudio = audioStats.find((s) => base64Length(s.size) > MAX_AUDIO_FILE_BASE64_BYTES);
  if (oversizeAudio) {
    console.error(
      `Error: ${path.basename(oversizeAudio.filePath)} (${asMb(oversizeAudio.size)} MB) is over the ~${asMb(MAX_AUDIO_FILE_BASE64_BYTES * 3 / 4)} MB audio limit for transcription.`,
    );
    process.exit(1);
  }

  const attachments = stats.map((s) => readAttachment(s.filePath, s.size));

  // Second, on the real serialized payload — the only way to account for the message
  // text, which the contract allows up to 50,000 characters and whose JSON-escaped
  // UTF-8 can dwarf any fixed allowance. Measuring beats reserving a constant that is
  // wrong in one direction or the other.
  const bodyBytes = Buffer.byteLength(
    JSON.stringify({
      hub_id: hubId,
      ...(message && { message }),
      ...(conversationId && { conversation_id: conversationId }),
      ...(attachments.length > 0 && { attachments }),
    }),
    'utf8',
  );
  if (bodyBytes > MAX_MESSAGE_BODY_BYTES) {
    console.error(
      `Error: the request is ~${asMb(bodyBytes)} MB, over the ${asMb(MAX_MESSAGE_BODY_BYTES)} MB per-request limit.`,
    );
    if (stats.length > 0) console.error(`Attachments: ${fileDetail()} (base64 adds about a third).`);
    process.exit(1);
  }

  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const result = await client.sendMessage(hubId, message, {
    conversationId,
    ...(attachments.length > 0 && { attachments }),
  });

  // Print result
  console.log(`Conversation: ${result.conversation_id}`);
  console.log(`Status: ${result.status}`);

  if (attachments.length > 0 && result.status !== 'error') {
    // The route fails the send if any attachment fails to upload, so a non-error
    // status means every one of these reached the agent.
    console.log(`Attached: ${attachments.map((a) => a.file_name).join(', ')}`);
  }

  if (result.status_reason) {
    console.log(`Reason: ${result.status_reason}`);
  }

  if (result.agent_reply) {
    console.log('');
    console.log(result.agent_reply.message_text);
  } else if (result.status === 'skipped') {
    console.log('(Agent did not respond)');
  } else if (result.status === 'processing') {
    console.log('(Agent is still working — the reply will arrive shortly; re-check the conversation)');
  } else if (result.status === 'quota_exceeded') {
    console.log('(Operations quota exceeded — the agent did not run)');
  } else if (result.status === 'error') {
    console.log('(Agent processing failed — user message was stored)');
  }
}
